/**
 * Singleton do beforeinstallprompt.
 *
 * Captura e armazena o evento, notifica subscribers de mudanças futuras.
 * O hook useInstallPrompt lê o estado inicial via getPrompt() e
 * se inscreve em mudanças via subscribe().
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type PromptChangeCallback = (prompt: BeforeInstallPromptEvent | null) => void;

const isBrowser = typeof window !== 'undefined';

let currentPrompt: BeforeInstallPromptEvent | null = null;
const subscribers: PromptChangeCallback[] = [];
const installedCallbacks: (() => void)[] = [];

function notifyPromptChange(prompt: BeforeInstallPromptEvent | null) {
  subscribers.forEach((fn) => fn(prompt));
}

function notifyInstalled() {
  installedCallbacks.forEach((fn) => fn());
}

// Recuperar evento capturado pelo script inline do index.html
if (isBrowser) {
  const earlyPrompt = (
    window as unknown as { __pwaInstallPrompt?: BeforeInstallPromptEvent }
  ).__pwaInstallPrompt;
  if (earlyPrompt) {
    currentPrompt = earlyPrompt;
    (window as unknown as { __pwaInstallPrompt: null }).__pwaInstallPrompt =
      null;
  }

  // Listener permanente para eventos tardios
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    currentPrompt = e as BeforeInstallPromptEvent;
    notifyPromptChange(currentPrompt);
  });

  // Limpar prompt quando o app for instalado
  window.addEventListener('appinstalled', () => {
    currentPrompt = null;
    notifyPromptChange(null);
    notifyInstalled();
  });
}

/**
 * Leitura síncrona do prompt atual. Para uso no useState initializer.
 */
export function getPrompt(): BeforeInstallPromptEvent | null {
  return currentPrompt;
}

/**
 * Inscreve-se em mudanças futuras do prompt.
 * Retorna função de unsubscribe.
 */
export function subscribe(cb: PromptChangeCallback): () => void {
  subscribers.push(cb);
  return () => {
    const idx = subscribers.indexOf(cb);
    if (idx >= 0) subscribers.splice(idx, 1);
  };
}

/**
 * Limpa o prompt após consumo (prompt() chamado).
 */
export function clearPrompt(): void {
  currentPrompt = null;
}

/**
 * Registra callback que dispara quando o app é instalado (appinstalled).
 * Sinal explícito de instalação, desacoplado de mudanças no prompt.
 * Retorna função de unsubscribe.
 */
export function onInstalled(cb: () => void): () => void {
  installedCallbacks.push(cb);
  return () => {
    const idx = installedCallbacks.indexOf(cb);
    if (idx >= 0) installedCallbacks.splice(idx, 1);
  };
}
